import React from 'react';
import { Divider, Input, Modal, Select, Slider, Switch } from 'antd';
import { TVoiceModel } from '@/types/TVoiceModel';
import { VoiceProvider } from 'src/enums/EVoiceProvider';
import {
	ACCENT_OPTIONS,
	EMOTION_LEVEL_OPTIONS,
	EMOTION_OPTIONS,
	EMOTIONAL_RANGE_OPTIONS,
	IMPRESSION_OPTIONS,
	INTONATION_OPTIONS,
	SPEED_OPTIONS,
	TONE_OPTIONS,
	WHISPERING_OPTIONS,
} from 'src/constants/voiceModels';
import { Option } from '@/interfaces/IVoice';

interface VoiceConfigModalProps {
	open: boolean;
	onCancel: () => void;
	onOk: () => void;
	data: TVoiceModel;
	voiceModel: string;
	setVoiceModel: (val: string) => void;
	filteredVoiceModels: Option[];
	speed: string;
	setSpeed: (val: string) => void;
	speedRime: number;
	setSpeedRime: (val: number) => void;
	emotion: string;
	setEmotion: (val: string) => void;
	level: string;
	setLevel: (val: string) => void;
	accent: string;
	setAccent: (val: string) => void;
	emotionalRange: string;
	setEmotionalRange: (val: string) => void;
	intonation: string;
	setIntonation: (val: string) => void;
	impression: string;
	setImpression: (val: string) => void;
	tone: string;
	setTone: (val: string) => void;
	whispering: string;
	setWhispering: (val: string) => void;
	voicePrompt: string;
	setVoicePrompt: (val: string) => void;
	customPauses: boolean;
	setCustomPauses: (val: boolean) => void;
	customPronunciation: boolean;
	setCustomPronunciation: (val: boolean) => void;
	temperature: number;
	setTemperature: (val: number) => void;
}

export const VoiceConfigModal: React.FC<VoiceConfigModalProps> = ({
	open,
	onCancel,
	onOk,
	data,
	voiceModel,
	setVoiceModel,
	filteredVoiceModels,
	speed,
	setSpeed,
	speedRime,
	setSpeedRime,
	emotion,
	setEmotion,
	level,
	setLevel,
	accent,
	setAccent,
	emotionalRange,
	setEmotionalRange,
	intonation,
	setIntonation,
	impression,
	setImpression,
	tone,
	setTone,
	whispering,
	setWhispering,
	voicePrompt,
	setVoicePrompt,
	customPauses,
	setCustomPauses,
	customPronunciation,
	setCustomPronunciation,
	temperature,
	setTemperature,
}) => {
	return (
		<Modal title="Voice Configuration" open={open} onCancel={onCancel} onOk={onOk}>
			<div className="flex flex-col overflow-y-auto max-h-[70vh] p-4">
				<div className="mb-3">
					<label htmlFor="voiceModel" className="mb-1 block font-medium">
						Voice Model
					</label>
					<Select
						value={voiceModel}
						id="voiceModel"
						style={{ width: '100%' }}
						onChange={(val: string) => setVoiceModel(val)}
						options={filteredVoiceModels}
					/>
				</div>

				{data.provider === VoiceProvider.Cartesia && (
					<>
						<div className="mb-3">
							<label htmlFor="speed" className="mb-1 block font-medium">
								Speed
							</label>
							<Select
								value={speed}
								id="speed"
								style={{ width: '100%' }}
								onChange={setSpeed}
								options={SPEED_OPTIONS}
							/>
						</div>

						<div className="mb-3">
							<label htmlFor="emotion" className="mb-1 block font-medium">
								Emotion Name
							</label>
							<Select
								value={emotion}
								style={{ width: '100%' }}
								onChange={setEmotion}
								options={EMOTION_OPTIONS}
							/>
						</div>

						<div className="mb-4">
							<label htmlFor="level" className="mb-1 block font-medium">
								Emotion Level
							</label>
							<Select
								value={level}
								style={{ width: '100%' }}
								onChange={setLevel}
								options={EMOTION_LEVEL_OPTIONS}
							/>
						</div>
					</>
				)}

				{data.provider === VoiceProvider.OpenAI && (
					<>
						<div className="mb-3">
							<label htmlFor="speed" className="mb-1 block font-medium">
								Speed
							</label>
							<Select
								value={speed}
								id="speed"
								style={{ width: '100%' }}
								onChange={setSpeed}
								options={SPEED_OPTIONS}
							/>
						</div>

						<div className="mb-3">
							<label htmlFor="accent" className="mb-1 block font-medium">
								Accent
							</label>
							<Select
								value={accent}
								style={{ width: '100%' }}
								onChange={setAccent}
								options={ACCENT_OPTIONS}
							/>
						</div>

						<div className="mb-3">
							<label htmlFor="emotionalRange" className="mb-1 block font-medium">
								Emotional Range
							</label>
							<Select
								value={emotionalRange}
								style={{ width: '100%' }}
								onChange={setEmotionalRange}
								options={EMOTIONAL_RANGE_OPTIONS}
							/>
						</div>

						<div className="mb-3">
							<label htmlFor="intonation" className="mb-1 block font-medium">
								Intonation
							</label>
							<Select
								value={intonation}
								style={{ width: '100%' }}
								onChange={setIntonation}
								options={INTONATION_OPTIONS}
							/>
						</div>

						<div className="mb-3">
							<label htmlFor="impression" className="mb-1 block font-medium">
								Impression
							</label>
							<Select
								value={impression}
								style={{ width: '100%' }}
								onChange={setImpression}
								options={IMPRESSION_OPTIONS}
							/>
						</div>

						<div className="mb-3">
							<label htmlFor="tone" className="mb-1 block font-medium">
								Tone
							</label>
							<Select
								value={tone}
								style={{ width: '100%' }}
								onChange={setTone}
								options={TONE_OPTIONS}
							/>
						</div>

						<div className="mb-4">
							<label htmlFor="whispering" className="mb-1 block font-medium">
								Whispering
							</label>
							<Select
								value={whispering}
								style={{ width: '100%' }}
								onChange={setWhispering}
								options={WHISPERING_OPTIONS}
							/>
						</div>
						<Divider />
						<div className="flex justify-center">OR</div>
						<div className="mb-4">
							<label htmlFor="voicePrompt" className="mb-1 block font-medium">
								Voice Prompt
							</label>
							<label className="mb-1 block text-xs text-gray-700">
								Enter your own voice prompt to configure and generate voice as per your requirement
							</label>
							<Input.TextArea
								value={voicePrompt}
								style={{ width: '100%' }}
								rows={4}
								placeholder='eg. "Speak in a cheerful and positive tone."'
								onChange={(e) => setVoicePrompt(e.target.value)}
							/>
						</div>
					</>
				)}

				{data.provider === VoiceProvider.RIME && data.model === 'arcana' && (
					<>
						<div className="mb-3">
							<label htmlFor="speed" className="mb-1 block font-medium">
								Temperature
							</label>
							<Slider
								min={0}
								step={0.01}
								max={1}
								className="mx-4"
								value={temperature}
								onChange={setTemperature}
								marks={{ 0: 'Deterministic', 0.75: 'Balanced', 1: 'Creative' }}
							/>
						</div>
					</>
				)}

				{data.provider === VoiceProvider.RIME && data.model === 'mistv2' && (
					<>
						<div className="mb-3">
							<label htmlFor="speed" className="mb-1 block font-medium">
								Speed
							</label>
							<Slider
								min={0.75}
								max={1.3}
								step={0.01}
								className="mx-4"
								value={speedRime}
								onChange={setSpeedRime}
								marks={{ 0.75: 'Faster', 1: 'Normal', 1.3: 'Slower' }}
							/>
						</div>

						<div className="mb-3">
							<label htmlFor="accent" className="mb-1 block font-medium">
								Custom Pauses
							</label>
							<Switch value={customPauses} onChange={setCustomPauses} />
						</div>

						<div className="mb-3">
							<label htmlFor="accent" className="mb-1 block font-medium">
								Custom Pronunciations
							</label>
							<Switch value={customPronunciation} onChange={setCustomPronunciation} />
						</div>
					</>
				)}
			</div>
		</Modal>
	);
};
